import { useMemo, useRef, useState } from 'react';
import { Download, Plus, Search, Trash2, Upload } from 'lucide-react';
import { Button, Input, Pagination } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import {
  REGION_AREA_NAME_MAX,
  buildRegionSurchargeTemplateCsv,
  duplicateAreaNames,
  normalizeAreaName,
  parseRegionSurchargeCsv,
  type RegionSurchargeRow,
} from '@/lib/domain/regionSurcharge';

export interface RegionSurchargeTableProps {
  rows: RegionSurchargeRow[];
  onChange: (rows: RegionSurchargeRow[]) => void;
  disabled: boolean;
  /** 비어 있을 때 한 번에 채울 예시 행. 없으면 버튼을 숨긴다. */
  seedRows?: () => RegionSurchargeRow[];
  /** 같은 화면에 여러 번 나올 때(국가그룹마다) 입력란을 구분하는 접두어. */
  labelPrefix?: string;
  testId?: string;
}

/** 한 페이지 행 수. 이관 데이터는 247곳이라 한 화면에 다 펼치면 대화상자가 끝없이 길어진다. */
export const REGION_PAGE_SIZE = 20;

const toFee = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
};

/**
 * 지역 추가배송비 입력 — 국내 배송비조건과 해외 자체요율 국가그룹이 함께 쓴다.
 * 규칙·검증·CSV 파싱은 `lib/domain/regionSurcharge` 에 있고, 여기서는 입력과 표시만 한다.
 *
 * 검색은 지역명에 들어 있는지(공백·대소문자 무시)로 거른다. 검색·페이지는 보이는 범위만 바꾸고, 수정은
 * 항상 전체 목록의 해당 행에 반영된다. 새 행은 맨 뒤에 붙고, 검색을 풀고 마지막 페이지로 가서 보여 준다.
 */
export const RegionSurchargeTable = ({
  rows,
  onChange,
  disabled,
  seedRows,
  labelPrefix = '',
  testId = 'region-surcharge-table',
}: RegionSurchargeTableProps) => {
  const { t } = useTranslation('common');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const tr = (key: string, fallback: string, opts: Record<string, unknown> = {}) =>
    t(`regionSurcharge.${key}`, { defaultValue: fallback, ...opts });

  // 전체 목록 인덱스를 달고 거른다 — 수정·삭제가 보이는 순서가 아니라 원래 행을 가리키게.
  const filtered = useMemo(() => {
    const q = normalizeAreaName(query);
    const indexed = rows.map((row, index) => ({ row, index }));
    return q === '' ? indexed : indexed.filter(({ row }) => normalizeAreaName(row.areaName).includes(q));
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / REGION_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * REGION_PAGE_SIZE, currentPage * REGION_PAGE_SIZE);
  const duplicates = useMemo(() => duplicateAreaNames(rows), [rows]);

  const updateRow = (index: number, patch: Partial<RegionSurchargeRow>) =>
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const removeRow = (index: number) => onChange(rows.filter((_, i) => i !== index));
  const addRow = () => {
    onChange([...rows, { areaName: '', fee: 0 }]);
    setQuery('');
    setPage(Math.ceil((rows.length + 1) / REGION_PAGE_SIZE));
  };

  // CSV 는 기존 목록을 교체한다(수백 곳을 한 번에 세팅하는 용도).
  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 같은 파일 재선택 허용
    if (!file) return;
    void file.text().then((text) => {
      const parsed = parseRegionSurchargeCsv(text);
      if (parsed.length === 0) return;
      onChange(parsed);
      setQuery('');
      setPage(1);
    });
  };

  const downloadTemplate = () => {
    const blob = new Blob([buildRegionSurchargeTemplateCsv()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'region-surcharge-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
        {tr('rule', '배송지 주소에 지역명이 들어 있으면 추가요금을 붙입니다. 여러 지역이 맞으면 가장 구체적인(긴) 지역 1건만 적용하고, 합산하지 않습니다.')}
      </p>

      {rows.length > REGION_PAGE_SIZE ? (
        <div className="flex items-center gap-2">
          <Search size={14} aria-hidden="true" style={{ color: 'var(--content-muted)' }} />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder={tr('searchPlaceholder', '지역명 검색')}
            className="h-8 max-w-[280px]"
            aria-label={`${labelPrefix}${tr('searchAria', '지역명 검색')}`}
          />
          <span className="text-[11px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
            {tr('count', '{{shown}} / 전체 {{total}}곳', { shown: filtered.length, total: rows.length })}
          </span>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-md" style={{ border: '1px solid var(--divider-soft)' }}>
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr style={{ background: 'var(--surface-raised)' }}>
              <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--content-muted)' }}>
                {tr('colArea', '지역명')}
              </th>
              <th className="w-[160px] px-3 py-2 text-left font-medium" style={{ color: 'var(--content-muted)' }}>
                {tr('colFee', '추가 배송비 (원)')}
              </th>
              <th className="w-[44px] px-2 py-2" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-3 py-3 text-center" style={{ color: 'var(--content-muted)' }}>
                  {rows.length === 0 ? tr('empty', '지역 추가배송비가 없습니다.') : tr('noMatch', '검색된 지역이 없습니다.')}
                </td>
              </tr>
            ) : null}
            {visible.map(({ row, index }) => (
              <tr key={row.id ?? `new-${index}`} style={{ borderTop: '1px solid var(--divider-soft)' }}>
                <td className="px-2 py-1.5">
                  <Input
                    value={row.areaName}
                    onChange={(e) => updateRow(index, { areaName: e.target.value })}
                    placeholder={tr('areaPlaceholder', '예: 제주특별자치도 제주시')}
                    maxLength={REGION_AREA_NAME_MAX}
                    disabled={disabled}
                    className="h-8"
                    aria-label={`${labelPrefix}${tr('areaAria', '지역명')} ${index + 1}`}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Input
                    type="number"
                    step="1"
                    min={0}
                    value={String(row.fee)}
                    onChange={(e) => updateRow(index, { fee: toFee(e.target.value) })}
                    disabled={disabled}
                    className="h-8 tabular-nums"
                    aria-label={`${labelPrefix}${tr('feeAria', '추가 배송비')} ${index + 1}`}
                  />
                </td>
                <td className="px-2 py-1.5 text-center">
                  {!disabled ? (
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      aria-label={`${labelPrefix}${tr('removeAria', '행 삭제')} ${index + 1}`}
                      style={{ color: 'var(--signal-danger)', cursor: 'pointer' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length > REGION_PAGE_SIZE ? (
        <Pagination
          total={filtered.length}
          currentPage={currentPage}
          pageSize={REGION_PAGE_SIZE}
          onPageChange={setPage}
          showGoTo={false}
        />
      ) : null}

      {duplicates.length > 0 ? (
        <p className="text-[11px]" style={{ color: 'var(--status-warn-fg)' }}>
          {tr('duplicate', '같은 지역명이 두 번 이상 있습니다: {{names}}. 어느 요금이 붙을지 정해지지 않으니 하나만 남기세요.', {
            names: duplicates.slice(0, 3).join(', ') + (duplicates.length > 3 ? ' …' : ''),
          })}
        </p>
      ) : null}

      {!disabled ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={addRow} data-testid={`${testId}-add`}>
            <Plus size={14} aria-hidden="true" />
            {tr('add', '행 추가')}
          </Button>
          {seedRows && rows.length === 0 ? (
            <Button variant="ghost" size="sm" onClick={() => onChange(seedRows())}>
              {tr('seed', '제주·울릉 예시 채우기')}
            </Button>
          ) : null}
          <span className="mx-1 h-4 w-px" style={{ background: 'var(--divider-strong)' }} aria-hidden="true" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid={`${testId}-upload`}>
            <Upload size={14} aria-hidden="true" />
            {tr('csvUpload', 'CSV 업로드')}
          </Button>
          <Button variant="ghost" size="sm" onClick={downloadTemplate}>
            <Download size={14} aria-hidden="true" />
            {tr('csvTemplate', '양식 다운로드')}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            onChange={onFileSelect}
            aria-hidden="true"
            tabIndex={-1}
          />
        </div>
      ) : null}
      {!disabled ? (
        <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
          {tr('csvHint', 'CSV 형식: 지역명,추가배송비 (헤더 1행 포함). 지역명,우편번호,추가배송비 3열도 받으며 가운데 열은 무시합니다. 업로드하면 기존 목록을 대체합니다.')}
        </p>
      ) : null}
    </div>
  );
};
